import { QueryDefinition, QueryStatus } from "@reduxjs/toolkit/query"
import { AsyncDataProps, AsyncErrorProps } from "../../utility/common/asyncHelper"
import { BaseQueryError, BaseQueryFn } from "@reduxjs/toolkit/src/query/baseQueryTypes"
import { SerializedError } from "@reduxjs/toolkit"
import { apiCallExceptionToSystemError } from "../../utility/common/apiCallExceptionToSystemError"
import { logError } from "../../utility/common/logError"
import { saveNotificationError } from "../../store/notifications/thunks"
import { useAppDispatch } from "../../store/hooks"
import { errorTranslation } from "../../locales/error"
import { useTranslation } from "react-i18next"

// Redux Toolkit prohibits import an original UseQueryStateDefaultResult. Therefore, I just made own
// eslint-disable-next-line
export type UseQueryStateBaseResult<D extends QueryDefinition<any, any, any, any>, R> = {
    /**
     * Where `data` tries to hold data as much as possible, also re-using
     * data from the last arguments passed into the hook, this property
     * will always contain the received data from the query, for the current query arguments.
     */
    currentData?: R
    /**
     * Query has not started yet.
     */
    isUninitialized: boolean
    /**
     * Query is currently loading for the first time. No data yet.
     */
    isLoading: boolean
    /**
     * Query is currently fetching, but might have data from an earlier request.
     */
    isFetching: boolean
    /**
     * Query has data from a successful load.
     */
    isSuccess: boolean
    /**
     * Query is currently in "error" state.
     */
    isError: boolean
    status: QueryStatus
    data?: R
    /**
     * The received error if applicable
     */
    error?:
        | SerializedError
        // Redux Toolkit prohibits import an original UseQueryStateDefaultResult. Therefore, I just made own
        // eslint-disable-next-line
        | (D extends QueryDefinition<any, infer BaseQuery, any, any> ? BaseQueryError<BaseQuery> : never)
    endpointName?: string
    /**
     * Time that the latest query started
     */
    startedTimeStamp?: number
    /**
     * Time that the latest query was fulfilled
     */
    fulfilledTimeStamp?: number
}

export interface AsyncQueryProps<
    QueryArg,
    BaseQuery extends BaseQueryFn,
    TagTypes extends string,
    ResultType,
    ReducerPath extends string = string,
    S = UseQueryStateBaseResult<QueryDefinition<QueryArg, BaseQuery, TagTypes, ResultType, ReducerPath>, ResultType>
> {
    query: S & { originalArgs?: QueryArg }
    defaultView?: JSX.Element
    processView?: JSX.Element
    errorView: (props: AsyncErrorProps) => JSX.Element
    errorMessage?: string
    children: (props: AsyncDataProps<ResultType>) => JSX.Element
    // Внимание validator может приводить к  неожиданным ререндорам, использовать можно только с не реактивными переменными.
    // Плохой пример убран в мре-https://gitlab.crafttalk.ru/aibotdev/core/opbot/-/merge_requests/9222
    validator?: (data: S & { originalArgs?: QueryArg }) => boolean
    emptyDataView?: JSX.Element
    sendNotification?: boolean
}

function AsyncQuery<
    QueryArg,
    BaseQuery extends BaseQueryFn,
    TagTypes extends string,
    ResultType,
    ReducerPath extends string = string
>(props: AsyncQueryProps<QueryArg, BaseQuery, TagTypes, ResultType, ReducerPath>) {
    const {
        query,
        processView,
        children,
        errorView,
        defaultView,
        validator,
        emptyDataView = null,
        errorMessage,
        sendNotification = false
    } = props
    const { data, error, isError, isSuccess, isLoading, isUninitialized } = query
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    if (isUninitialized && defaultView) {
        return defaultView
    }

    if (isLoading && processView) {
        return processView
    }

    if (isError && error) {
        // TODO: add errorMessage to all AsyncQuery's
        const message = errorMessage || "Error occurred"
        if (sendNotification) {
            const SystemError = apiCallExceptionToSystemError(message, t(errorTranslation.serverError))
            saveNotificationError(dispatch, SystemError)
        }

        return errorView({ message: message })
    }

    if (validator) {
        return isSuccess && data && validator(query) ? children({ data }) : emptyDataView
    } else {
        return isSuccess && data ? children({ data }) : emptyDataView
    }
}

export default AsyncQuery
